import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LayoutFixHome from "../components/LayoutFixHome";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import ProcessPerClientTable from "../components/ProcessesPerClientTable";
import ProcessPerClientCardView from "../components/ProcessPerClientCardView";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import { useNavigate } from "react-router-dom";
import { RoutesConfig } from "../router/types";
import { useSelectedItemStore } from "../store/useSelectedItemStore";
import { toast } from "react-toastify";
import { getClientByCode, getClientProcess } from "../services/clientService";
import { useUserStore } from "../store/userStore";
import { SearchProcessModal } from "../components/SearchProcessModal";

interface ProcessPerClientProps {}

const ProcessPerClientPage: React.FC<ProcessPerClientProps> = (
  props: ProcessPerClientProps
) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const selectedItemId = useSelectedItemStore((state) => state.selectedItemId);
  // const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [resultClientProcesses, setResultClientProcesses] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [showProcessSearchModal, setProcessSearchModal] = useState(false);
  const [clientInfo, setClientInfo] = useState<any>();
  const profile = useUserStore((state) => state.profile);

  const handleSearchProcess = () => {
    setProcessSearchModal(true);
  };

  const getClientInfo = async () => {
    if (!selectedItemId) return;
    try {
      const data = await getClientByCode(selectedItemId);
      setClientInfo(data);
    } catch (err) {
      toast.error("Erro ao buscar info cliente");
      console.error("Erro ao buscar info cliente:", err);
    }
  };

  const getClientProcesses = async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const data = await getClientProcess(clientInfo?.INSC_CNPJ, nextCursor);

      setResultClientProcesses((prevProcesses: any) => {
        const newClientProcess = data.items.filter(
          (newClient: any) =>
            !prevProcesses.some(
              (existing: any) => existing.CD_PROCESSO === newClient.CD_PROCESSO
            )
        );
        return [...prevProcesses, ...newClientProcess];
      });

      setNextCursor(data.nextCursor);
      setHasMore(data.hasNextPage);
    } catch (err) {
      toast.error("Erro ao buscar processos do cliente");
      console.error("Erro ao buscar processos do cliente:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getClientInfo();
  }, []);

  useEffect(() => {
    if (clientInfo?.INSC_CNPJ) {
      getClientProcesses();
    }
  }, [clientInfo]);

  const handleLoadMore = () => {
    if (hasMore && nextCursor) {
      getClientProcesses();
    }
  };

  const handleBack = () => {
    navigate(RoutesConfig.HOME.path);
  };

  const reloadClientProcesses = async () => {
    setResultClientProcesses([]);
    setNextCursor(null);
    setHasMore(true);
    // setIsFirstLoad(true);
    setIsLoading(true);
    try {
      const data = await getClientProcess(clientInfo?.INSC_CNPJ, null);

      if (data?.items?.length) {
        setResultClientProcesses(data.items);
        setNextCursor(data.nextCursor);
        setHasMore(data.hasNextPage);
      } else {
        setResultClientProcesses([]);
        setHasMore(false);
      }
    } catch (err) {
      toast.error("Erro ao recarregar processos");
      console.error("Erro ao recarregar processos:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect(() => {
  //   if (isFirstLoad && resultClientProcesses) {
  //     setIsFirstLoad(false);
  //   }
  // }, [resultClientProcesses]);

  return (
    <>
      <LayoutFixHome>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "29px",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
              marginTop: {
                sm: "75px",
                xs: "75px",
                md: 0,
              },
            }}
          >
            <Box
              onClick={handleBack}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <ExpandCircleDownOutlinedIcon
                style={{ transform: "rotate(-270deg)" }}
                sx={{ color: "#0A0C33", height: "30px", width: "30px" }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#0A0C33" }}
            >
              Processos por Cliente
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
              marginTop: {
                sm: "75px",
                xs: "75px",
                md: 0,
              },
            }}
          >
            {profile[0] !== "analista" && (
              <Button
                sx={{
                  textTransform: "none",
                  color: "#326AFD",
                  borderRadius: "27px",
                  fontSize: "16px",
                  fontWeight: "700",
                  width: "202px",
                  border: "1px solid #326AFD",
                }}
                onClick={() => setProcessSearchModal(true)}
              >
                Buscar Processo
              </Button>
            )}
            <Button
              sx={{
                textTransform: "none",
                backgroundColor: "#326AFD",
                color: "#FFFFFF",
                borderRadius: "27px",
                fontSize: "16px",
                fontWeight: "700",
                width: "202px",
              }}
              onClick={() => navigate(RoutesConfig.MEUSPROCESSOS.path)}
            >
              <InsertLinkIcon
                sx={{ transform: "rotate(45deg)", marginRight: "5px" }}
              />
              Meus Processos
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: "#F6F6F6",
              boxShadow: "0px 3px 6px #00000029",
              position: "relative",
              marginBottom: isMobile ? "10px" : "1%",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                width: "24px",
                height: "100%",
                backgroundColor: "#326AFD",
                borderRadius: "12px 0px 0px 12px",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginLeft: "23px",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "#0A0C33",
                  marginRight: "23px",
                }}
              >
                {clientInfo?.NOME}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                REF: {selectedItemId}
              </Typography>
            </Box>
          </Box>
          {isMobile ? (
            <ProcessPerClientCardView
              list={resultClientProcesses}
              loadMoreData={handleLoadMore}
              isLoading={isLoading}
              reloadData={reloadClientProcesses}
            />
          ) : (
            <ProcessPerClientTable
              list={resultClientProcesses}
              loadMoreData={handleLoadMore}
              isLoading={isLoading}
              reloadData={reloadClientProcesses}
            />
          )}
          {isLoading && resultClientProcesses.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              padding={4}
            >
              <CircularProgress
                size={48}
                thickness={4}
                style={{ color: "#326AFD" }}
              />
            </Box>
          )}
          <SearchProcessModal
            open={showProcessSearchModal}
            onClose={() => setProcessSearchModal(false)}
          />
        </Box>
      </LayoutFixHome>
    </>
  );
};

export default ProcessPerClientPage;
